import apiClient from './api';
import {
  RaceEvent,
  CrewMember,
  EventCrewAssociation,
  EnhancedComplianceStatus,
  RequiredDocument,
  RaceSummary,
  DocumentRequirement,
  ComplianceDashboardData,
} from '../types';

/**
 * Service module for event operations including document Q&A and crew management.
 *
 * Requirements: 4.1, 5.1, 5.2, 5.3, 6.1, 7.1
 */

export interface EventDocumentStatus {
  documentId: string;
  extractionStatus: 'processing' | 'complete' | 'failed';
  sectionCount: number;
  uploadedAt: string;
  fileName: string;
  error?: string;
}

export interface AmendmentStatus extends EventDocumentStatus {
  amendmentNumber: number;
}

export interface EventDocumentStatusResponse {
  raceId: string;
  noticeOfRace: EventDocumentStatus | null;
  sailingInstructions: EventDocumentStatus | null;
  amendments: AmendmentStatus[];
}

export interface EventQASource {
  documentType: string;
  sectionNumber: string;
}

export interface EventQAResponse {
  answer: string;
  sources: EventQASource[];
  regulationSectionsSearched: number;
  eventSectionsSearched: number;
  tokenUsage?: {
    input: number;
    output: number;
    total: number;
  };
  cost?: number;
  timing?: {
    loadSections: number;
    generateEmbedding: number;
    similaritySearch: number;
    claudeResponse: number;
    total: number;
  };
}

export interface BoatContext {
  hullType: string;
  lengthMeters: number;
}

export interface EventDocumentUploadResponse {
  message: string;
  documentId: string;
  documentType: string;
  status: string;
  amendmentNumber?: number;
}

export type EventDocumentType = 'notice_of_race' | 'sailing_instructions' | 'amendment';

/**
 * Data for adding a crew member to an event.
 * Requirements: 5.2
 */
export interface AddCrewToEventData {
  crewId: string;
  role?: string;  // e.g., 'skipper', 'navigator', 'crew'
}

export const eventService = {
  // ---- Event Crew Management ----

  /**
   * Fetch available race events.
   * Requirements: 4.1
   */
  getEvents: async (): Promise<RaceEvent[]> => {
    const response = await apiClient.get<{ events?: unknown[] }>('/events');
    // API returns { events: [...] }, extract the array
    const rawEvents = response.data.events || response.data;
    if (!Array.isArray(rawEvents)) return [];
    
    // Normalize snake_case to camelCase
    return rawEvents.map((e: unknown) => {
      const event = e as Record<string, unknown>;
      return {
        raceId: event.race_id || event.raceId,
        name: event.name,
        year: event.year,
        category: event.category,
        date: event.date,
        requirements: event.requirements || [],
        documents: event.documents || [],
        createdAt: event.created_at || event.createdAt,
        // Keep requirements_count for display
        requirements_count: event.requirements_count,
      } as RaceEvent & { requirements_count?: number };
    });
  },

  /**
   * Fetch crew list for a specific event.
   * Requirements: 5.1
   */
  getEventCrew: async (raceId: string): Promise<CrewMember[]> => {
    const response = await apiClient.get<{ crew_members?: unknown[]; crew?: unknown[] }>(
      `/events/${encodeURIComponent(raceId)}/crew`
    );
    // API may return { crew_members: [...] } or { crew: [...] }
    const rawData = response.data.crew_members || response.data.crew || response.data;
    if (!Array.isArray(rawData)) return [];
    
    // Convert snake_case to camelCase
    return rawData.map((item: unknown) => {
      const crew = item as Record<string, unknown>;
      return {
        crewId: crew.crew_id || crew.crewId,
        userId: crew.user_id || crew.userId,
        firstName: crew.first_name || crew.firstName,
        lastName: crew.last_name || crew.lastName,
        dateOfBirth: crew.date_of_birth || crew.dateOfBirth,
        asNumber: crew.as_number || crew.asNumber,
        asVerified: crew.as_verified || crew.asVerified || false,
        certificates: Array.isArray(crew.certificates) ? crew.certificates.map((cert: unknown) => {
          const c = cert as Record<string, unknown>;
          return {
            certificateId: c.certificate_id || c.certificateId,
            type: c.type,
            name: c.name,
            expiryDate: c.expiry_date || c.expiryDate,
            documentId: c.document_id || c.documentId,
          };
        }) : [],
        nextOfKin: crew.next_of_kin || crew.nextOfKin,
        plbHexNumber: crew.plb_hex_number || crew.plbHexNumber,
        phoneNumber: crew.phone_number || crew.phoneNumber,
        inviteStatus: crew.invite_status || crew.inviteStatus,
        inviteSentAt: crew.invite_sent_at || crew.inviteSentAt,
        createdAt: crew.created_at || crew.createdAt,
        updatedAt: crew.updated_at || crew.updatedAt,
      } as CrewMember;
    });
  },

  /**
   * Add a crew member to an event.
   * Requirements: 5.2
   */
  addCrewToEvent: async (
    raceId: string,
    data: AddCrewToEventData
  ): Promise<EventCrewAssociation> => {
    // Convert to snake_case for API
    const snakeData = {
      crew_id: data.crewId,
      role: data.role,
    };
    const response = await apiClient.post<{ association?: EventCrewAssociation }>(
      `/events/${encodeURIComponent(raceId)}/crew`,
      snakeData
    );
    return response.data.association || response.data as unknown as EventCrewAssociation;
  },

  /**
   * Remove a crew member from an event.
   * Requirements: 5.3
   */
  removeCrewFromEvent: async (raceId: string, crewId: string): Promise<void> => {
    await apiClient.delete(
      `/events/${encodeURIComponent(raceId)}/crew/${encodeURIComponent(crewId)}`
    );
  },

  /** Send a WhatsApp invite to a crew member for an event. */
  sendInvite: async (raceId: string, crewId: string): Promise<void> => {
    await apiClient.post(
      `/events/${encodeURIComponent(raceId)}/crew/${encodeURIComponent(crewId)}/invite`
    );
  },

  /**
   * Fetch compliance status with required documents for an event.
   * Requirements: 6.1
   */
  getEventCompliance: async (raceId: string): Promise<EnhancedComplianceStatus> => {
    const response = await apiClient.get<EnhancedComplianceStatus>(
      `/events/${encodeURIComponent(raceId)}/compliance`
    );
    return response.data;
  },

  /**
   * Fetch required documents list for an event.
   * Requirements: 7.1
   */
  getEventDocuments: async (raceId: string): Promise<RequiredDocument[]> => {
    const response = await apiClient.get<RequiredDocument[]>(
      `/events/${encodeURIComponent(raceId)}/documents`
    );
    return response.data;
  },

  // ---- Event Document Q&A ----
  /** Ask a question about a specific event. */
  askEventQuestion: async (
    raceId: string,
    question: string,
    boatContext?: BoatContext,
    signal?: AbortSignal
  ): Promise<EventQAResponse> => {
    const payload: Record<string, unknown> = { question };
    if (boatContext) {
      payload.boat_context = {
        hull_type: boatContext.hullType,
        length_meters: boatContext.lengthMeters,
      };
    }
    const response = await apiClient.post<EventQAResponse>(
      `/events/${encodeURIComponent(raceId)}/ask`,
      payload,
      { signal }
    );
    return response.data;
  },

  /** Get document status for an event. */
  getEventDocumentStatus: async (
    raceId: string
  ): Promise<EventDocumentStatusResponse> => {
    const response = await apiClient.get<EventDocumentStatusResponse>(
      `/events/${encodeURIComponent(raceId)}/documents/status`
    );
    return response.data;
  },

  /** Upload an event document (NoR, SI, or Amendment). */
  uploadEventDocument: async (
    raceId: string,
    file: File,
    documentType: EventDocumentType
  ): Promise<EventDocumentUploadResponse> => {
    // Read file as base64
    const fileContent = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await apiClient.post<EventDocumentUploadResponse>(
      `/admin/races/${encodeURIComponent(raceId)}/documents`,
      {
        file_name: file.name,
        file_content: fileContent,
        document_type: documentType,
      }
    );
    return response.data;
  },

  /** Delete an event document (NoR, SI, or amendment). */
  deleteEventDocument: async (
    raceId: string,
    documentType: string
  ): Promise<void> => {
    await apiClient.delete(
      `/admin/races/${encodeURIComponent(raceId)}/documents/${encodeURIComponent(documentType)}`
    );
  },

  // ---- Compliance Dashboard (Phase 2) ----

  /**
   * Fetch full compliance dashboard data for an event.
   * Requirements: 10.1, 11.4, 12.1
   */
  getComplianceDashboard: async (raceId: string): Promise<ComplianceDashboardData> => {
    const response = await apiClient.get<{
      race_summary: Record<string, unknown>;
      requirements: Record<string, unknown>[];
      overall_progress: Record<string, unknown>;
    }>(`/events/${encodeURIComponent(raceId)}/dashboard`);
    
    // Normalize snake_case to camelCase
    const data = response.data;
    return {
      raceSummary: {
        raceId: data.race_summary.race_id as string,
        name: data.race_summary.name as string,
        date: data.race_summary.date as string | undefined,
        distance: data.race_summary.distance as string | undefined,
        category: data.race_summary.category as string,
        source: data.race_summary.source as 'nor' | 'race_record',
      },
      requirements: (data.requirements || []).map((req) => ({
        requirementId: req.requirement_id as string,
        documentType: req.document_type as string,
        description: req.description as string,
        category: req.category as 'crew' | 'boat',
        requiredQuantity: req.required_quantity as number,
        currentCount: req.current_count as number,
        status: req.status as 'complete' | 'partial' | 'missing',
        linkedDocuments: ((req.linked_documents as Record<string, unknown>[]) || []).map((doc) => ({
          documentId: doc.document_id as string,
          documentType: doc.document_type as string,
          crewId: doc.crew_id as string | undefined,
          crewName: doc.crew_name as string | undefined,
          verificationStatus: doc.verification_status as 'pending' | 'verified' | 'failed' | 'expired',
          expiryDate: doc.expiry_date as string | undefined,
          isExpiringSoon: doc.is_expiring_soon as boolean | undefined,
        })),
      })),
      overallProgress: {
        totalRequirements: data.overall_progress.total_requirements as number,
        completedRequirements: data.overall_progress.completed_requirements as number,
        percentComplete: data.overall_progress.percent_complete as number,
      },
    };
  },

  /**
   * Fetch race summary for an event.
   * Requirements: 10.1, 10.2, 10.3
   */
  getRaceSummary: async (raceId: string): Promise<RaceSummary> => {
    const response = await apiClient.get<{
      race_id: string;
      name: string;
      date?: string;
      distance?: string;
      category: string;
      source: 'nor' | 'race_record';
    }>(`/events/${encodeURIComponent(raceId)}/summary`);
    
    const data = response.data;
    return {
      raceId: data.race_id,
      name: data.name,
      date: data.date,
      distance: data.distance,
      category: data.category,
      source: data.source,
    };
  },

  /**
   * Fetch NoR-extracted document requirements for an event.
   * Requirements: 11.1, 11.4
   */
  getNoRRequirements: async (raceId: string): Promise<DocumentRequirement[]> => {
    const response = await apiClient.get<{
      requirements: Record<string, unknown>[];
    }>(`/events/${encodeURIComponent(raceId)}/requirements`);
    
    return (response.data.requirements || []).map((req) => ({
      requirementId: req.requirement_id as string,
      documentType: req.document_type as string,
      description: req.description as string,
      category: req.category as 'crew' | 'boat',
      requiredQuantity: req.required_quantity as number,
      currentCount: req.current_count as number || 0,
      status: req.status as 'complete' | 'partial' | 'missing' || 'missing',
      linkedDocuments: [],
    }));
  },
};
